<script lang="ts">
  import {
    arrow,
    computePosition,
    flip,
    offset,
    shift,
    type Placement,
  } from '@floating-ui/dom'
  import { getContext, onMount, type Snippet } from 'svelte'

  type Props = {
    children: Snippet
    placement?: Placement
  }

  const { children, placement: placementProps }: Props = $props()

  const dropdownContext = getContext<DropdownContext>('dropdownContext')

  const computeTooltipPosition = async (
    triggerElement: HTMLButtonElement,
    containerElement: HTMLDivElement,
    arrowElement: HTMLDivElement,
  ) => {
    const { x, y, middlewareData, placement } = await computePosition(
      triggerElement,
      containerElement,
      {
        placement: placementProps,
        middleware: [
          offset(8),
          flip(),
          shift({ padding: 16 }),
          arrow({ element: arrowElement }),
        ],
      },
    )
    Object.assign(containerElement.style, {
      left: `${x}px`,
      top: `${y}px`,
    })

    if (middlewareData.arrow) {
      const [mainPlacement, secondaryPlacement] = placement.split('-')

      const arrowMainPosition = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }

      const arrowSecondaryPosition = {
        top: {
          end: 'right',
          start: 'left',
        },
        right: {
          end: 'bottom',
          start: 'top',
        },
        bottom: {
          end: 'right',
          start: 'left',
        },
        left: {
          end: 'bottom',
          start: 'top',
        },
      }

      const staticSide: (typeof arrowMainPosition)[keyof typeof arrowMainPosition]
        = arrowMainPosition[mainPlacement as keyof typeof arrowMainPosition]

      const secondaryStaticSize: (typeof arrowMainPosition)[keyof typeof arrowMainPosition]
        = arrowSecondaryPosition[mainPlacement as keyof typeof arrowSecondaryPosition]?.[
          secondaryPlacement as 'start' | 'end'
        ] as (typeof arrowMainPosition)[keyof typeof arrowMainPosition]

      Object.assign(arrowElement.style, {
        [staticSide]: '-4px',
        [secondaryStaticSize]: '1rem',
      })
    }
  }

  $effect(() => {
    if (
      dropdownContext.containerElement
      && dropdownContext.triggerElement
      && dropdownContext.arrowElement
    ) {
      computeTooltipPosition(
        dropdownContext.triggerElement,
        dropdownContext.containerElement,
        dropdownContext.arrowElement,
      )
    }
  })

  const onDocumentClick = (e: MouseEvent) => {
    if (
      dropdownContext.isOpen
      && !dropdownContext.containerElement?.contains(e.target as Node)
      && !dropdownContext.triggerElement?.contains(e.target as Node)
    ) {
      dropdownContext.isOpen = false
    }
  }

  onMount(() => {
    document.addEventListener('click', onDocumentClick)

    return () => {
      document.removeEventListener('click', onDocumentClick)
    }
  })
</script>

{#if dropdownContext.isOpen}
  <div bind:this={dropdownContext.containerElement} class="container">
    <div bind:this={dropdownContext.arrowElement} class="arrow"></div>
    <div class="content">
      {@render children()}
    </div>
  </div>
{/if}

<style lang="scss">
  .container {
    color: var(--dropdown-foreground);
    left: 0;
    position: absolute;
    top: 0;
    width: max-content;
    z-index: 10;

    .arrow {
      background: var(--dropdown-background);
      height: 8px;
      position: absolute;
      transform: rotate(45deg);
      width: 8px;
      z-index: -1;
    }

    .content {
      background-color: var(--dropdown-background);
      align-items: stretch;
      border-radius: 0.5rem;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      outline: 1px solid var(--dropdown-stroke);
    }
  }
</style>
